// Output tab, the hero of a compile: a verdict banner (proven, refused, or
// divergence), the taproot ADDRESS in a mono row with a copy button + network
// chip, and a compact key/value grid of the supporting keys and counts. When the
// gate refuses we explain why and point at the Certification tab.
//
// The verdict keys off the gate's own decision (gate.mayProceed) rather than the
// presence of an address, so the banner can never contradict the Certification
// tab. The one exception is a cleared gate on a compile that then failed to
// assemble: every leaf is proven but there is nothing to fund, so it says that
// instead of "safe to fund".

import type { ReactNode } from "react";
import { ShieldCheck, ShieldAlert, ShieldX, AlertOctagon } from "lucide-react";
import { useIde, selectActiveContract, argsPathFor } from "../store";
import { networkDef } from "../network";
import type { CompileResult, Gate } from "../types";
import { CopyButton, EmptyState, KvRow, SectionHead, parseExterns, runHint, trunc } from "./shared";

type VerdictTone = "proven" | "unproven" | "divergence" | "info";

interface VerdictFace {
  tone: VerdictTone;
  icon: ReactNode;
  title: string;
  body: ReactNode;
}

/** What the verdict needs to point a user at the args file when externs are
 *  unbound, instead of an empty Certification tab. */
interface VerdictCtx {
  goCert: () => void;
  goProblems: () => void;
  externs: { name: string; type: string }[];
  openArgs: () => void;
  argsLabel: string;
  hasArgs: boolean;
}

const ICON = { size: 18, strokeWidth: 1.85 } as const;

/** Human-readable reasons the funding gate refused, derived from the flags. */
function refusalReasons(gate: Gate): string[] {
  const reasons: string[] = [];
  if (gate.unproven > 0) {
    reasons.push(
      `${gate.unproven} ${gate.unproven === 1 ? "leaf could" : "leaves could"} not be proven over the full domain`,
    );
  }
  if (gate.coverageGap) {
    reasons.push("a coverage gap remains: not every reachable spend path is certified");
  }
  if (reasons.length === 0) reasons.push("the funding gate did not clear");
  return reasons;
}

function verdictFace(result: CompileResult, ctx: VerdictCtx): VerdictFace {
  const gate = result.gate;
  const { goCert } = ctx;

  if (result.internalError) {
    return {
      tone: "divergence",
      icon: <AlertOctagon {...ICON} />,
      title: "Internal compiler error",
      body: result.internalError,
    };
  }

  if (gate?.divergence) {
    return {
      tone: "divergence",
      icon: <ShieldX {...ICON} />,
      title: "Divergence: funding refused",
      body: (
        <>
          A leaf's certified behavior contradicts the source, so no fundable address is produced.{" "}
          <button type="button" className="pn-link" onClick={goCert}>
            See Certification
          </button>{" "}
          for the diverging leaf.
        </>
      ),
    };
  }

  // The gate only decides whether certification permits funding. A later stage
  // can still fail, taproot layout being the usual one, and then there is no
  // address to fund even though every leaf is proven. Saying "safe to fund"
  // here would be a lie, so name what actually happened.
  if (gate?.mayProceed && !result.ok && !result.address) {
    return {
      tone: "unproven",
      icon: <ShieldAlert {...ICON} />,
      title: "Proven, but not assembled",
      body: (
        <>
          Every spend path is certified, but the contract could not be assembled into a taproot
          output, so there is no address.{" "}
          <button type="button" className="pn-link" onClick={ctx.goProblems}>
            See Problems
          </button>{" "}
          for what blocked it.
        </>
      ),
    };
  }

  // Proven is the gate's own verdict (mayProceed), independent of whether this
  // target also assembled an address. Divergence is already handled above.
  if (gate?.mayProceed) {
    return {
      tone: "proven",
      icon: <ShieldCheck {...ICON} />,
      title: "Proven, safe to fund",
      body: result.address
        ? "Every spend path is certified over its full domain. The address below commits to exactly this contract."
        : "Every spend path is certified over its full domain.",
    };
  }

  // No gate at all: the compile only reached the template level because externs
  // are unbound (or bound to invalid values). This is NOT a refusal -- there are
  // no leaves to certify yet, so never send the user to an empty Certification
  // tab. Point at the args file and name what still needs a value.
  if (!gate) {
    const { externs, openArgs, argsLabel, hasArgs } = ctx;
    if (externs.length > 0) {
      const names = externs.map((e) => e.name).join(", ");
      const one = externs.length === 1;
      return {
        tone: "info",
        icon: <ShieldAlert {...ICON} />,
        title: "Needs values to fund",
        body: (
          <>
            {one ? "An extern needs a value" : "These externs need values"} before this contract can
            be certified and funded: <code className="pn-mono-inline">{names}</code>.{" "}
            {hasArgs ? (
              <>
                Set {one ? "it" : "them"} in{" "}
                <button type="button" className="pn-link" onClick={openArgs}>
                  {argsLabel}
                </button>
                , then compile.
              </>
            ) : (
              <>
                Create <code className="pn-mono-inline">{argsLabel}</code> with {one ? "its" : "their"}{" "}
                value{one ? "" : "s"}, then compile.
              </>
            )}
          </>
        ),
      };
    }
    return {
      tone: "info",
      icon: <ShieldAlert {...ICON} />,
      title: "No fundable address",
      body: "This compile passed template-level checks but did not assemble a fundable address.",
    };
  }

  // gate exists, is not a divergence, and did not clear: a genuine refusal.
  const reasons = refusalReasons(gate);
  return {
    tone: "unproven",
    icon: <ShieldAlert {...ICON} />,
    title: "Refused, not safe to fund",
    body: (
      <>
        {reasons.length === 1 ? (
          <>The funding gate refused because {reasons[0]}.</>
        ) : (
          <>
            The funding gate refused:
            <ul className="pn-out-reasons">
              {reasons.map((r, i) => (
                <li key={i}>{r}.</li>
              ))}
            </ul>
          </>
        )}{" "}
        <button type="button" className="pn-link" onClick={goCert}>
          Open Certification
        </button>{" "}
        to see which leaves need attention.
      </>
    ),
  };
}

export function OutputPanel() {
  const result = useIde((s) => s.result);
  const status = useIde((s) => s.status);
  const compileError = useIde((s) => s.compileError);
  const network = useIde((s) => s.prefs.network);
  const setBottomTab = useIde((s) => s.setBottomTab);
  const contract = useIde(selectActiveContract);
  const openFile = useIde((s) => s.openFile);
  const argsPath = contract ? argsPathFor(contract.path) : null;
  const hasArgs = useIde((s) => (argsPath ? !!s.files[argsPath] : false));

  if (compileError) {
    return (
      <div className="pn-scroll">
        <div className="pn-banner pn-banner--divergence" role="alert">
          <div className="pn-banner-main">
            <div className="pn-banner-title">Compile failed</div>
            <div className="pn-banner-sub">{compileError}</div>
          </div>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className="pn-scroll">
        <EmptyState
          icon={<ShieldCheck size={22} strokeWidth={1.5} />}
          title={status === "compiling" ? "Compiling…" : "No compile yet"}
          hint={runHint("the address, gate verdict and keys")}
        />
      </div>
    );
  }

  // The source (or its args) has errors and did not compile -- don't show a gate
  // verdict or a misleading "needs values"; point at Problems for the details.
  if (!result.ok) {
    const errors = result.diagnostics.filter((d) => d.severity === "error").length;
    return (
      <div className="pn-scroll">
        <div className="pn-banner pn-banner--divergence" role="alert">
          <div className="pn-banner-main">
            <div className="pn-banner-title">Has errors</div>
            <div className="pn-banner-sub">
              {errors > 0
                ? `${errors === 1 ? "1 error" : `${errors} errors`} to fix. `
                : "The contract did not compile. "}
              <button type="button" className="pn-link" onClick={() => setBottomTab("problems")}>
                Open Problems
              </button>{" "}
              to see {errors === 1 ? "it" : "them"}.
            </div>
          </div>
        </div>
      </div>
    );
  }

  const goCert = () => setBottomTab("certification");
  const goProblems = () => setBottomTab("problems");
  const externs = contract ? parseExterns(contract.content) : [];
  const openArgs = () => {
    if (argsPath) openFile(argsPath);
  };
  const argsLabel = argsPath ? (argsPath.split("/").pop() ?? argsPath) : "an args file";
  const face = verdictFace(result, { goCert, goProblems, externs, openArgs, argsLabel, hasArgs });
  const netLabel = networkDef(network).label.toLowerCase();
  const leafCount = result.leaves?.length ?? 0;

  return (
    <div className="pn-scroll">
      <div
        className={`pn-banner pn-banner--${face.tone}`}
        role={face.tone === "proven" || face.tone === "info" ? undefined : "alert"}
      >
        <div className="pn-banner-main">
          <div className="pn-banner-title">{face.title}</div>
          <div className="pn-banner-sub">{face.body}</div>
        </div>
      </div>

      {result.address && (
        <div className="pn-addr">
          <div className="pn-addr-head">
            <span className="label-caps">Taproot address</span>
            <span className={`pn-netchip pn-netchip--${network}`}>{netLabel}</span>
          </div>
          <div className="pn-addr-row">
            <span className="pn-addr-text">{result.address}</span>
            <CopyButton value={result.address} label="" title="Copy address" />
          </div>
        </div>
      )}

      <SectionHead title="Details" />
      <dl className="pn-kv">
        {result.outputKey && (
          <KvRow label="Output key" mono copy={result.outputKey}>
            <span title={result.outputKey}>{trunc(result.outputKey)}</span>
          </KvRow>
        )}
        <KvRow label="Spend paths">
          {leafCount} script {leafCount === 1 ? "leaf" : "leaves"}
          {result.gate?.coverageGap && <span className="pn-tag pn-tag--warn">coverage gap</span>}
        </KvRow>
        <KvRow label="Bound externs">
          {result.boundExterns} {result.boundExterns === 1 ? "value" : "values"} from args
        </KvRow>
      </dl>
    </div>
  );
}
